import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';

import { SectionBlock } from '../../../../blocks/SectionBlock';
import { DatePicker } from '../../../../common/DatePicker';
import { Sprint } from '../../types';
import { getSprintForDate } from '../../utils/dailyProgresshelper';

import { DailyProgressContent } from './DailyProgressContent';
import { createDatePickerProps } from './helpers';

interface DailyProgressProps {
  memberId?: string;
  employeeId?: string;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  selectedSprint: Sprint | null;
  setSelectedSprint: (sprint: Sprint | null) => void;
  sprints: Sprint[] | undefined;
  isLoading: boolean;
}

/**
 * DailyProgress component displays a calendar and daily progress timeline
 * It allows users to navigate through different dates and see team member activities
 */
export function DailyProgress({
  memberId,
  employeeId,
  selectedDate,
  setSelectedDate,
  selectedSprint,
  setSelectedSprint,
  sprints,
  isLoading,
}: DailyProgressProps): JSX.Element {
  const handleDateChange = useCallback(
    (date: Date): void => {
      // Check if date is within the available sprint range
      const earliestSprintDate = new Date(
        sprints?.[sprints.length - 1]?.startDate || '',
      );

      if (date >= earliestSprintDate) {
        setSelectedDate(date);

        // Find the appropriate sprint for the selected date
        const newSprint = getSprintForDate(date, selectedSprint, sprints);

        // If a new sprint was found and it's different from the current sprint
        if (newSprint && newSprint !== selectedSprint) {
          setSelectedSprint(newSprint);
          toast.success(
            `You are now viewing ${newSprint?.name} based on the selected date`,
          );
        }
      }
    },
    [sprints, selectedSprint, setSelectedDate, setSelectedSprint],
  );

  // Create date picker props with sprints data
  const datePickerProps = useMemo(
    () =>
      createDatePickerProps(
        selectedDate,
        handleDateChange,
        sprints,
        selectedSprint,
      ),
    [selectedDate, handleDateChange, sprints, selectedSprint],
  );

  // Prepare props for content component to prevent unnecessary re-renders
  const contentProps = useMemo(
    () => ({
      isLoading,
      sprints,
      selectedDate,
      memberId,
      employeeId,
    }),
    [isLoading, sprints, selectedDate, memberId, employeeId],
  );

  return (
    <SectionBlock
      title="Daily Progress"
      headerContent={<DatePicker {...datePickerProps} />}
      skipContentContainer
    >
      <DailyProgressContent {...contentProps} />
    </SectionBlock>
  );
}
